import useFluxStore from "@armus/use-flux-store";
import OverActions from "data/OverActions";
import userStore from "data/userStore";
import Dispatcher from "Dispatcher";
import BroadcastListener from "lib/broadcast/BroadcastListener";

import { backboneConnect } from "lib/common/hoc/BackboneReact/backboneConnect";
import { title as genericXmlExportPasswordAdminTitle } from "lib/genericXmlExportPassword/GenericXmlExportPasswordAdmin";
import LockListener from "lib/lock/LockListener";
import promisify from "lib/promisify";
import Services from "lib/Services";
import PropTypes from "prop-types";
import React from "react";
import DevNavItemsContainer from "./dev/DevNavItemsContainer";
import DropDown from "./DropDown";
import WhoAmIListener from "../lib/whoami/WhoAmIListener";

class KeyedItem extends React.PureComponent {

    constructor(props) {
        super(props);
        this.onClick = this.onClick.bind(this);
    }

    onClick(event) {
        event.preventDefault();
        const {
            onClick,
            itemKey: key,
        } = this.props;
        onClick && onClick(key);
    }

    render() {
        const {
            itemKey: key,
            name,
            active,
            sectionKey
        } = this.props;
        return active
            ? <li className="active" data-section={sectionKey}>
                <a href="#" data-key={key}>
                    {name}
                </a>
            </li>
            : <li data-section={sectionKey}>
                <a
                    href="#"
                    onClick={this.onClick}
                    onTouchStart={this.onClick}
                    data-key={key}
                >
                    {name}
                </a>
            </li>;
    }
}

KeyedItem.propTypes = {
    onClick: PropTypes.func.isRequired,
    itemKey: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    active: PropTypes.bool,
    sectionKey: PropTypes.string,
};

class KeyedSection extends React.PureComponent {

    render() {
        const {
            items,
            label,
            onClick,
            divider
        } = this.props;
        if (items == null || items.length === 0) return null;
        const sectionKey = label.replace(/[^a-z0-9]+/gi, " ")
            .trim()
            .replace(/ /g, "-");
        return <React.Fragment>
            {divider && <li className="divider" />}
            <li className="dropdown-header section" data-section={sectionKey}>{label}</li>
            {items.map(v =>
                <KeyedItem
                    itemKey={v.key}
                    onClick={onClick}
                    sectionKey={sectionKey}
                    {...v}
                />)}
        </React.Fragment>;
    }

}

KeyedSection.propTypes = {
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            active: PropTypes.bool,
        })),
    onClick: PropTypes.func.isRequired,
    divider: PropTypes.bool,
};

class KeyedDropdown extends React.PureComponent {

    render() {
        const {
            items,
            label,
            className,
            onClick,
        } = this.props;
        if (items == null || items.length === 0) return null;
        return <DropDown
            label={label}
            className={className}
        >
            {items.map(v =>
                <KeyedItem
                    itemKey={v.key}
                    onClick={onClick}
                    {...v}
                />)}
        </DropDown>;
    }

}

KeyedDropdown.propTypes = {
    ...KeyedSection.propTypes,
    className: PropTypes.string,
};

function generateApplications(user) {
    const applications = [];

    if (user.canUseDE) {
        applications.push({
            key: "de",
            name: "Data Entry",
        });
    }
    if (user.canUseDqr) {
        applications.push({
            key: "dqr",
            name: "DQR",
        });
    }

    if (user.canUseHex) {
        applications.push({
            key: "hex",
            name: "Harvest/Export",
        });
    }

    return applications;
}

/*******************************************************************************
 *
 * NavbarView uses `process.env.NODE_ENV` in several places to change the way
 * nav works in non-production. If you want the production experience in
 * development, search and replace the "development" string to some garbage,
 * just make sure you don't commit it that way. :)
 *
 * This dance is needed (instead of using a flag) because the environment
 * variable is evaluated at _compile_ time, and thus changes the generated
 * bundle. Using a flag would only ensure that the flag itself was initialized
 * to a constant in the bundles, it wouldn't be able to tree-shake the dev-only
 * code from the production bundle, because the flag itself won't be evaluated
 * until runtime.
 *
 ******************************************************************************/
export function NavbarView(props) {
    const {
        lookups,
        userInfo,
        selectOrg,
        onLookupClick,
        onValidationAdminClick,
        onHiddenFieldsAdminClick,
        onAboutClick,
        onPreferencesClick,
        // these are optional, indicating whether to show
        onGenericXmlPasswordAdmin,
        onBroadcastAdmin,
        onLogoutClick,
        onReauthClick,
    } = props;

    // this is just asinine, but such is our kludgey frankenapp.
    React.useEffect(() => {
        if (!document || !document.body || !document.body.classList) return;
        if (onReauthClick) {
            document.body.classList.add("security-mode-client");
        } else {
            document.body.classList.remove("security-mode-client");
        }
    }, [onReauthClick]);

    const orgs = userInfo.allOrgs;
    const navTree = userInfo.navTree;
    const activeOrg = userInfo.activeOrg;
    const showValidationAdmin = key =>
        onValidationAdminClick(key, activeOrg.key);
    const showHiddenFieldsAdmin = key =>
        onHiddenFieldsAdminClick(key, activeOrg.key);
    const applications = generateApplications(userInfo);
    const globalLookups = lookups != null ? lookups.models.map(it => ({
        key: it.get("key"),
        name: it.get("pluralName") || it.get("name"),
    })) : null;
    const navTreeItems = navTree == null ? null :
        navTree.items.map(n =>
            <li key={n.label}>
                <a href={n.url}>
                    {n.label}
                </a>
            </li>);
    const adminItems = [];
    if (onGenericXmlPasswordAdmin) {
        adminItems.push(
            <KeyedItem
                key="gxep"
                onClick={onGenericXmlPasswordAdmin}
                itemKey="gxep"
                name={genericXmlExportPasswordAdminTitle}
            />);
    }
    if (onBroadcastAdmin) {
        adminItems.push(
            <KeyedItem
                key="broadcast"
                onClick={onBroadcastAdmin}
                itemKey="broadcast"
                name={"Admin Broadcast"}
            />);
    }
    if (globalLookups != null && globalLookups.length > 0) {
        adminItems.push(
            <KeyedSection
                key="global-lookups"
                label="Global Lookups"
                items={globalLookups}
                onClick={onLookupClick}
                divider={adminItems.length > 0}
            />);
    }
    if (userInfo.validationAdminImpls != null && userInfo.validationAdminImpls.length > 0) {
        adminItems.push(
            <KeyedSection
                key="validation-admin"
                label="Validation Administration"
                items={userInfo.validationAdminImpls}
                onClick={showValidationAdmin}
                divider={adminItems.length > 0}
            />);
    }
    if (userInfo.hiddenFieldAdminImpls != null && userInfo.hiddenFieldAdminImpls.length >= 0) {
        adminItems.push(
            <KeyedSection
                key="hidden-field-admin"
                label="Hidden Field Administration"
                items={userInfo.hiddenFieldAdminImpls}
                onClick={showHiddenFieldsAdmin}
                divider={adminItems.length > 0}
            />);
    }

    // noinspection HtmlUnknownTarget
    return (
        <div className="navbar-inner">
            <BroadcastListener />
            <LockListener />
            <WhoAmIListener
                user={userInfo.username}
                org={activeOrg.key}
            />
            <div className="container-fluid">
                <div className="navbar-header">
                    <button type="button" className="navbar-toggle"
                            data-toggle="collapse"
                            data-target="#navbar-collapse">
                        <span className="sr-only">Toggle navigation</span>
                        <span className="icon-bar" />
                        <span className="icon-bar" />
                        <span className="icon-bar" />
                    </button>
                    <a className="navbar-brand" href="#">
                        <img src="images/logo.png" alt="ARMUS" />
                        ARMUS
                    </a>
                </div>
                <div className="collapse navbar-collapse" id="navbar-collapse">
                    <ul className="nav navbar-nav">
                        {applications.length === 1
                            ? <li>
                                <p className={"navbar-text"}>
                                    HYBRID {applications[0].name}
                                </p>
                            </li>
                            : <DropDown label="HYBRID"
                                        className="menu-hybrid control">
                                {applications.map(a =>
                                    <li key={a.name}>
                                        <a href={`#${a.key}`}>{a.name}</a>
                                    </li>)}
                            </DropDown>}
                        {orgs &&
                        <React.Fragment>
                            {process.env.NODE_ENV !== "development" &&
                            <li className="navbar-text">Organization:</li>}
                            {orgs.length === 1
                                ? <li className="navbar-text">
                                    {activeOrg.name}
                                    {" "}
                                    {onReauthClick && <button
                                        className="btn btn-dark btn-xs"
                                        onClick={onReauthClick}
                                    >
                                        Reauth
                                    </button>}
                                </li>
                                : <KeyedDropdown
                                    label={activeOrg.name}
                                    className="menu-orgs"
                                    items={orgs}
                                    onClick={selectOrg}
                                />}
                        </React.Fragment>
                        }
                    </ul>
                    <ul className="nav navbar-nav navbar-right">
                        {process.env.NODE_ENV === "development" &&
                        <DevNavItemsContainer />}
                        <DropDown
                            label={process.env.NODE_ENV === "development" ? null : "Administration"}
                            icon="cog"
                            className="menu-settings"
                            align="right"
                            children={adminItems}
                        />
                        <DropDown
                            label={process.env.NODE_ENV === "development" ? null : "Applications"}
                            icon="th-list"
                            className="menu-navtree"
                            align="right"
                            children={navTreeItems}
                        />
                        <li className="navbar-text greeting">Welcome
                            Back, {userInfo.displayName}</li>
                        <DropDown
                            icon="user"
                            className="menu-user"
                            align="right"
                        >
                            <KeyedItem
                                onClick={onAboutClick}
                                itemKey="about"
                                name="About"
                            />
                            {userInfo.canUseDE && <KeyedItem
                                onClick={onPreferencesClick}
                                itemKey="preferences"
                                name="Preferences"
                            />}
                            <li><a href="https://armussupport.zendesk.com"
                                   target="_blank"
                                   rel="noopener noreferrer">ARMUS Support</a>
                            </li>
                            <li className="divider" />
                            {onLogoutClick && <KeyedItem
                                onClick={onLogoutClick}
                                itemKey="logout"
                                name="Logout"
                            />}
                            {onReauthClick && <KeyedItem
                                onClick={onReauthClick}
                                itemKey="reauth"
                                name="Reauthorize [TEST]"
                            />}
                        </DropDown>
                    </ul>
                </div>
            </div>
        </div>
    );
}

const passthroughPropTypes = {
    selectOrg: PropTypes.func.isRequired,
    lookups: PropTypes.object,
    onLookupClick: PropTypes.func.isRequired,
    onAboutClick: PropTypes.func.isRequired,
    onPreferencesClick: PropTypes.func.isRequired,
    onGenericXmlPasswordAdmin: PropTypes.func,
    onBroadcastAdmin: PropTypes.func,
    onLogoutClick: PropTypes.func,
    onReauthClick: PropTypes.func,
    onValidationAdminClick: PropTypes.func.isRequired,
    onHiddenFieldsAdminClick: PropTypes.func.isRequired,
};

NavbarView.propTypes = {
    ...passthroughPropTypes,
    userInfo: PropTypes.shape({
        displayName: PropTypes.string.isRequired,
        canUseDE: PropTypes.bool.isRequired,
        canUseDqr: PropTypes.bool.isRequired,
        canUseHex: PropTypes.bool.isRequired,
        navTree: PropTypes.object,
        allOrgs: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                active: PropTypes.bool.isRequired,
            })
        ).isRequired,
        activeOrg: PropTypes.shape({
            key: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        }).isRequired,
        validationAdminImpls: PropTypes.arrayOf(
            PropTypes.object
        ).isRequired,
        hiddenFieldAdminImpls: PropTypes.arrayOf(
            PropTypes.object
        ).isRequired,
    }).isRequired,
};

const NavbarViewContainer = props => {
    const userInfo = useFluxStore(
        () => ({
            displayName: userStore.getDisplayName(),
            superuser: userStore.isSuperuser(),
            canUseDE: userStore.canUseDE(),
            canUseDqr: userStore.canUseDqr(),
            canUseHex: userStore.canUseHex(),
            anyPrefMan: userStore.isPrefManForAnything(),
            navTree: userStore.getNavTree(),
            allOrgs: userStore.getAllOrgs(),
            activeOrg: userStore.getActiveOrg(),
            username: userStore.getUsername(),
            validationAdminImpls: userStore.getImplsWithValidationAdmin(),
            hiddenFieldAdminImpls: userStore.getImplsWithHiddenFieldAdmin(),
            isRealSession: userStore.isRealSession(),
        }),
        [userStore],
    );
    props = {...props};
    if (!userInfo.anyPrefMan) {
        delete props.onGenericXmlPasswordAdmin;
    }
    if (!userInfo.superuser) {
        delete props.onBroadcastAdmin;
    }
    if (!userInfo.canUseDE) {
        delete props.lookups;
    }
    if (userInfo.isRealSession) {
        delete props.onReauthClick;
    } else {
        delete props.onLogoutClick;
    }
    return <NavbarView {...props} userInfo={userInfo} />;
};

NavbarViewContainer.propTypes = passthroughPropTypes;

function onReauthClick() {
    Backbone.dispatcher.trigger("load:test-reauth");
}

const config = (props) => ({
    selectOrg: (key) => {
        Dispatcher.dispatch({
            type: OverActions.EXTERNAL_NAV,
            message: "Loading...",
            callback: () => {
                promisify(Services.saveActiveOrgs([key])).then( () => {
                    window.location.hash = ""; //  kludge to remove any path, as it'll be invalid w/ new org
                    window.location.reload();
                });
            }
        });
    },
    lookups: {
        listenOn: "appModel"
    },
    onLookupClick: (key) =>
        Backbone.dispatcher.trigger("load:global_lookup", key),
    onValidationAdminClick: (key, org) =>
        Backbone.dispatcher.trigger("load:validation_filter", org, key),
    onHiddenFieldsAdminClick: (key, org) =>
        Backbone.dispatcher.trigger("load:hidden_field_admin", org, key),
    onAboutClick: () =>
        Backbone.dispatcher.trigger("load:about"),
    onPreferencesClick: () =>
        Backbone.dispatcher.trigger("load:preferences"),
    onGenericXmlPasswordAdmin: () =>
        Backbone.dispatcher.trigger("load:genericXmlPasswordAdmin"),
    onBroadcastAdmin: () =>
        Backbone.dispatcher.trigger("load:broadcastAdmin"),
    onLogoutClick: () =>
        Backbone.dispatcher.trigger("confirm:user:logout", props.appModel),
    onReauthClick,
});

export const BackboneNavbarView = backboneConnect(config)(NavbarViewContainer);



// WEBPACK FOOTER //
// ./app/views/NavbarView.js